import { useState } from 'react';
import { UseFormSetError } from 'react-hook-form';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { changeUserPassword, checkUserPassword, checkValidUserPassword } from '@api/userProfile';

import { IResponseError } from '@interfaces/errors/apiErrors';
import { IChangePasswordDTO, ICheckPasswordDTO } from '@interfaces/user/dto/userProfileDTO';
import { IUserPasswordValues } from '@interfaces/user/userProfile';

import { ESnackbarTypes, EYupErrorTypes } from '@enums/common';
import { EUserPasswordValues } from '@enums/userProfile';

import { CHANGE_PASSWORD, CHECK_PASSWORD, CHECK_VALID_PASSWORD } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseUserPasswordProps {
	setError: UseFormSetError<IUserPasswordValues>;
	toggleChangePasswordModal: () => void;
}

export const useMutateUserPassword = ({ setError, toggleChangePasswordModal }: IUseUserPasswordProps) => {
	const dispatch = useAppDispatch();

	const [isPassCurrentPassword, setIsPassCurrentPassword] = useState(false);
	const [isPassNewPassword, setIsPassNewPassword] = useState(false);

	const {
		mutate: checkPassword,
		isLoading: isLoadingCheckPassword,
	} = useMutation<ICheckPasswordDTO>({
		mutationKey: [CHECK_PASSWORD],
		mutationFn: checkUserPassword,
		onSuccess: () => setIsPassCurrentPassword(true),
		onError: (err: IResponseError) => {
			const errorText = err.errors[0].message;

			setError(EUserPasswordValues.currentPassword, {
				type: EYupErrorTypes.required,
				message: errorText,
			});
		},
	});

	const {
		mutate: checkValidNewPassword,
		isLoading: isLoadingCheckValidNewPassword,
	} = useMutation<ICheckPasswordDTO>({
		mutationKey: [CHECK_VALID_PASSWORD],
		mutationFn: checkValidUserPassword,
		onSuccess: () => setIsPassNewPassword(true),
		onError: (err: IResponseError) => {
			const errorText = err.errors[0].message;

			setError(EUserPasswordValues.newPassword, {
				type: EYupErrorTypes.required,
				message: errorText,
			});
		},
	});

	const {
		mutate: changePassword,
		isLoading: isLoadingChangePassword,
	} = useMutation<IChangePasswordDTO>({
		mutationKey: [CHANGE_PASSWORD],
		mutationFn: changeUserPassword,
		onSuccess: () => {
			toggleChangePasswordModal();

			dispatch(setSnackbar({
				message: responseMessages.changePassword,
				type: ESnackbarTypes.success,
			}));
		},
		onError: (err: IResponseError) => {
			const errorText = err.errors[0].message;

			setError(EUserPasswordValues.confirmPassword, {
				type: EYupErrorTypes.required,
				message: errorText,
			});
		},
	});

	return {
		checkPassword,
		changePassword,
		checkValidNewPassword,
		isPassCurrentPassword,
		isPassNewPassword,
		isLoadingPassword: isLoadingCheckPassword || isLoadingChangePassword || isLoadingCheckValidNewPassword,
	};
};
